import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { ProductosFitosanitariosFormDefinition } from './form-control/form-definition';
import { Utils } from '../../../../../common/utils';
import moment from 'moment';

import { ProductosModel } from '../../../../../models/productos/productos.model';
import { ColInterface } from '../../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../../common/components/common-list-table/col-styles.interface';
import { TreeNode } from 'primeng/api';
import { baseRequest, baseSentence } from '../../../../../common/classes/request-builder';
import { OtrasDenominacionesApiService } from '../../../../../service/api/otrasdenominaciones-api.service';
import { ReactiveFormRequestCoordinator } from 'src/app/common/components/reactive-form-builder/form-request-flow.coordinator';
import { ProductCatalogCacheService } from 'src/app/service/cache/product-catalog-cache.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import type { DropdownData } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { FormField, FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';

const PRODUCT_FORM_CACHE_TTL_MS = 300000;

@Component({
	selector: 'app-stock-formulario-productos',
	standalone: false,
	templateUrl: './productos-form.component.html',
	styleUrls: ['./productos-form.component.scss'],
})

export class ProductosFormComponent extends BaseForm<ProductosModel> implements OnInit, OnDestroy {
	public form: ProductosFitosanitariosFormDefinition = new ProductosFitosanitariosFormDefinition(
		this.getType(),
		this.getModel() as Record<string, string>,
		null,
	);

	public formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();

	public vademecumTableRequest = new RequestHandler<ProductosModel[]>();

	public fecha_caducidad = '';

	public searchValue = {
		term: '',
	};

	private _dropdownsJustLoaded = false;

    private vademecumSearchTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		public dashboardEvents: DashboardService,
		public formApi: FormCommonApiService,
		public productosApi: ProductosApiService,
		public otrasDenominaciones: OtrasDenominacionesApiService,
		public override router: Router,
		public override route: ActivatedRoute,
		private readonly productCatalogCache: ProductCatalogCacheService,
	) {
		super(
			route,
			router,
			dashboardEvents,
			productosApi.productos.PUT,
			productosApi.productos.POST,
			'productos',
			'Actualizar Fitosanitario',
			'Alta de Producto Fitosanitario',
		);
		this.formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();
	}

	ngOnInit(): void {
		this.dropdownsFinalized = false;

		this.productosApi.productos.POST.reset();
		this.productosApi.productos.PUT.reset();

		this.initFormData();
		this.softInit(this.getType());
	}

	public formChanges(formModel: Partial<ProductosModel>, tag: string): void {
    this.form.model = {
        ...this.form.model,
        ...formModel,
    };

    switch (tag) {
        case 'num_registro':
            this.handleRegisterNumberChange(formModel.num_registro);
            break;

        case 'nombre':
            this.setVademecumSearchValue(this.getVademecumSearchTerm());
            break;

        case 'otra_denominacion_comun':
            this.syncProductDropdownSource(true);
            break;
    }
}

	public productSelected(row: any): void {
		const product = (row?.data ?? row) as ProductosModel;

		if (!product) {
			return;
		}

		this.form.model.num_registro = product;
		this.form.model.nombre = product.nombre_comercial;
		this.form.model.materia_activa = product.composicion ?? product.materia_activa;
		this.fecha_caducidad = product.fecha_caducidad ?? '';

		this.formBuilderRef.updateForm(this.form.model, true);
	}

	public globalConditionalStyle(
		_cellValue: string,
		_colDefinition: ColInterface,
		_colGroup: ColInterface[],
		rowValue: TreeNode<ProductosModel>,
	): ColStylesInterface {
		const baseStyle: any = {
			color: 'black',
			backgroundColor: '#ffffff',
			borderBottom: '1px solid #dddddd',
		};

		const fechaCaducidad = Utils.toDate(rowValue.data?.fecha_caducidad ?? '');
		const fechaInscripcion = Utils.toDate(rowValue.data?.fecha_inscripcion ?? '');

		const diffCaducidad = moment().diff(fechaCaducidad, 'months');
		const diffInscripcion = moment().diff(fechaInscripcion, 'months');

		if (diffInscripcion < 12) {
			baseStyle.backgroundColor = '#cbdecb';
		}

		if (diffCaducidad > 12) {
			baseStyle.backgroundColor = '#222222';
			baseStyle.color = 'white';
		} else if (diffCaducidad > 6) {
			baseStyle.backgroundColor = '#ff7f7f';
		} else if (diffCaducidad >= 0) {
			baseStyle.backgroundColor = '#ffcf78';
		}

		return { rowStyle: baseStyle };
	}

	public globalVisualTransform(value: string): string {
		return value;
	}

    public override ngOnDestroy(): void {
        if (this.vademecumSearchTimer !== null) {
            clearTimeout(this.vademecumSearchTimer);
        }

        Utils.unsuscribe([
            this.formApi.magramaDetalleProducto,
        ]);

        Utils.unsuscribe([
            this.formApi.magramaProducto
        ]);
    }

	private initFormData(): Promise<boolean> {
		return Promise.all([
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('productos-vademecum'),
				() => baseRequest(this.formApi.productosVademecum) as Promise<ProductosModel[]>,
			),
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('otras-denominaciones'),
				() => baseRequest(this.otrasDenominaciones.otrasDenominacionesGET) as Promise<ProductosModel[]>,
			),
		])
			.then(([standardProducts, otherDenominations]) => {
				this.vademecumTableRequest.emitResponse(standardProducts);

				this.assignDropdownData(
					this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>,
					standardProducts,
					(item: ProductosModel) => {
						const level = Utils.checkIfProductIsValid(item.fecha_caducidad, 'fitosanitario');

						return `${level} [${item.referencia}] ${item.nombre_comercial}`;
					},
				);

				this.assignDropdownData(
					this.form.lists.denominaciones_otras as DropdownData<ProductosModel>,
					otherDenominations,
					(item: ProductosModel) => `[${item.num_registro}] ${item.denominaciones_comunes} - ${item.nombre_comercial}`,
				);

				this.form.lists.productos =
					this.form.model.otra_denominacion_comun?.toString() === '1'
						? this.form.lists.denominaciones_otras
						: this.form.lists.denominaciones_estandar;

				this.dropdownsFinalized = true;
				this.dropdownLoadTrigger.next();

				if ([FormType.EDITION, FormType.DUPLICATION].includes(this.getType())) {
					this.restoreSelectedProductForEdition();
				}

				return true;
			})
			.catch((error: unknown) => {
				console.error('Error loading product form dropdowns:', error);
				this.dropdownsFinalized = true;
				this.dropdownLoadTrigger.next();

				return false;
			});
	}

	private assignDropdownData<T>(target: DropdownData<T>, source: T[], label: (item: T) => string): void {
		const options = baseSentence(source, label, true);

		target.filtered = [...options];
		target.values = [...options];
	}

	private restoreSelectedProductForEdition(): void {
		const { num_registro: numRegistro = 0, nombre = '' } = this.getModel();
		const esDenominacionOtra = this.form.model.otra_denominacion_comun?.toString() === '1';
		const listas = esDenominacionOtra ? this.form.lists.denominaciones_otras : this.form.lists.denominaciones_estandar;

		this.form.lists.productos = listas;

		const selected =
			listas.values.find((item) => {
				const value = item.value as ProductosModel | null;

				if (!value) {
					return false;
				}

				return esDenominacionOtra
					? value.num_registro === numRegistro && value.denominaciones_comunes === nombre
					: value.referencia === numRegistro;
			})?.value ?? null;

		this.form.lists.productos.selected = selected;
		this.form.model.num_registro = selected;

		this._dropdownsJustLoaded = true;

		this.formBuilderRef.updateForm(this.form.model, true);
	}

 
private getVademecumSearchTerm(): string {
    if (!this.isOtherCommonDenominationEnabled()) {
        return this.form.model.nombre ?? '';
    }

    const selected = this.form.lists.denominaciones_otras.selected;

    if (selected === null || Array.isArray(selected)) {
        return this.form.model.nombre ?? '';
    }

    return selected.nombre_comercial ?? this.form.model.nombre ?? '';
}


private handleRegisterNumberChange(value: unknown): void {
    if (this._dropdownsJustLoaded) {
        this._dropdownsJustLoaded = false;
        return;
    }

    const selectedProduct = this.getSelectedProduct(value);

    if (selectedProduct === null) {
        this.setVademecumSearchValue('');
        return;
    }

    const productName = this.isOtherCommonDenominationEnabled()
        ? selectedProduct.denominaciones_comunes ?? selectedProduct.nombre_comercial ?? ''
        : selectedProduct.nombre_comercial ?? selectedProduct.nombre ?? '';

    const activeMatter = selectedProduct.composicion ?? selectedProduct.materia_activa ?? '';

    this.form.model.nombre = productName;
    this.form.model.materia_activa = activeMatter;
    this.fecha_caducidad = selectedProduct.fecha_caducidad ?? '';

    this.formBuilderRef.updateForm({
        nombre: productName,
        materia_activa: activeMatter,
    }, false);

    this.setVademecumSearchValue(this.getSelectedProductSearchTerm(selectedProduct));
}

private getSelectedProduct(value: unknown): ProductosModel | null {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return null;
    }

    return value as ProductosModel;
}

private getSelectedProductSearchTerm(product: ProductosModel): string {
    const values = [
        product.referencia,
        product.num_registro,
        product.nombre_comercial,
        product.nombre,
    ];

    return values
        .map((item) => String(item ?? '').trim())
        .find((item) => item.length > 0) ?? '';
}


private setVademecumSearchValue(term: string): void {
    this.searchValue = {
        term: term.trim(),
    };
}

	private getProductCacheConfig(name: string): {
		namespace: string;
		name: string;
		ttlMs: number;
		userScope: string;
		clientScope: string;
	} {
		return {
			namespace: 'producto-add-form',
			name,
			ttlMs: PRODUCT_FORM_CACHE_TTL_MS,
			userScope: String(StorageManager.getUser()?.id ?? 'anonymous'),
			clientScope: String(StorageManager.getClient()?.id ?? 'no-client'),
		};
	}

    private syncProductDropdownSource(clearSelectedProduct: boolean): void {
    const useOtherCommonDenomination = this.isOtherCommonDenominationEnabled();

    const dropdownData = useOtherCommonDenomination
        ? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
        : this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;

    this.form.lists.productos = dropdownData;
    this.updateRegisterNumberFieldDropdown(dropdownData, useOtherCommonDenomination);

    if (clearSelectedProduct) {
        this.clearSelectedProductAfterDenominationChange();
    }

    this.dropdownLoadTrigger.next();
}
private updateRegisterNumberFieldDropdown(
    dropdownData: DropdownData<ProductosModel>,
    useOtherCommonDenomination: boolean,
): void {
    const field = this.getRegisterNumberField();

    if (field === null || field.input.dropdownConfiguration === undefined) {
        return;
    }

    field.input.data = dropdownData;
    field.input.dropdownConfiguration.valuePrimaryKey = useOtherCommonDenomination
        ? 'num_registro'
        : 'referencia';
}

private getRegisterNumberField(): FormField<ProductosModel> | null {
    for (const section of this.form.formFields.sections ?? []) {
        const found = section.fields.find((field) => field.name === 'num_registro') as FormField<ProductosModel> | undefined;

        if (found !== undefined) {
            return found;
        }
    }

    return null;
}

private clearSelectedProductAfterDenominationChange(): void {
    this.form.lists.productos.selected = null;
    this.fecha_caducidad = '';
    this.searchValue = { term: '' };

    this.form.model = {
        ...this.form.model,
        num_registro: null,
        nombre: '',
        materia_activa: '',
    };

    this.formBuilderRef.updateForm({
        num_registro: null,
        nombre: '',
        materia_activa: '',
    }, false);
}

private isOtherCommonDenominationEnabled(value: unknown = this.form.model.otra_denominacion_comun): boolean {
    return value === '1' || value === 1 || value === true;
}


	override goBack(): void {
		window.history.back();
	}

    

	public get productoSeleccionado(): ProductosModel | null {
		const selected = this.form.lists.productos.selected;

		if (selected === null || Array.isArray(selected)) {
			return null;
		}

		return selected;
	}
}
